import { PreviewContainer, PreviewImage, ModalImageWrapper, ModalImage, OpenSourceButton } from './styles';
import React, { useEffect, useState } from 'react';
import Modal from '../../../Modal';
import { ReactComponent as OpenIcon } from '../../svg/square-link.svg';
import utils from 'utils';

interface ImageProps {
  mimeType: string;
  url: string;
  imageBase64?: string;
}

const THUMB_WIDTH = 260;
const THUMB_QUALITY = 0.6;

const compressToThumbnail = (src: string): Promise<{ thumb: string; height: number }> =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const ratio = THUMB_WIDTH / img.naturalWidth;
      const thumbHeight = Math.round(img.naturalHeight * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_WIDTH;
      canvas.height = thumbHeight;
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ thumb: canvas.toDataURL('image/jpeg', THUMB_QUALITY), height: thumbHeight });
    };
    img.onerror = () => resolve({ thumb: src, height: 148 });
    img.src = src;
  });

export const Image: React.FC<ImageProps> = ({ mimeType, url, imageBase64 }) => {
  const t = utils.intl.useTranslator();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [thumbSrc, setThumbSrc] = useState<string>();
  const [thumbHeight, setThumbHeight] = useState<number>();

  const isImage = mimeType?.startsWith('image/');
  const displayTitle = utils.app.textTranslated('image', t, 'Image');

  const imageSrc = imageBase64 ? `data:image/png;base64,${imageBase64}` : url;

  useEffect(() => {
    if (!isImage) return;
    compressToThumbnail(imageSrc).then(({ thumb, height }) => {
      setThumbSrc(thumb);
      setThumbHeight(height);
    });
  }, [imageSrc, isImage]);

  const handleOpenSource = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    window.open(url, '_blank');
  };

  if (!isImage) return null;

  return (
    <>
      <PreviewContainer style={{ height: thumbHeight }} onClick={() => setIsModalOpen(true)}>
        {thumbSrc && <PreviewImage src={thumbSrc} alt={utils.app.textTranslated('imagePreview', t, 'Image preview')} />}
      </PreviewContainer>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} closingOnOverlayClick={true} isDraggable={false}>
        <div>
          <Modal.Header title={displayTitle} />
          <Modal.Content>
            <ModalImageWrapper>
              <ModalImage src={imageSrc} alt={displayTitle} />
              {!imageBase64 && (
                <OpenSourceButton href={url} target="_blank" rel="noopener noreferrer" onClick={handleOpenSource}>
                  <OpenIcon width={16} height={16} />
                </OpenSourceButton>
              )}
            </ModalImageWrapper>
          </Modal.Content>
        </div>
      </Modal>
    </>
  );
};
